import { Controller } from "@hotwired/stimulus";

// Presents a one-shot delight (the narwhal + confetti) and cleans itself up.
export default class extends Controller {
  static targets = ["comet"];

  static CONFETTI_COLORS = ["#22d3ee", "#0062ff", "#a78bfa", "#fbbf24", "#34d399", "#f472b6"];
  static CONFETTI_LIFETIME = 1400;
  static APEX_FRACTION = 0.4;

  connect() {
    if (this.#prefersReducedMotion) {
      this.element.remove();
      return;
    }

    const animationMs = this.#animationDuration;
    this.burstTimeout = setTimeout(
      () => this.burst(),
      animationMs * this.constructor.APEX_FRACTION
    );
    this.timeout = setTimeout(
      () => this.dismiss(),
      animationMs + this.constructor.CONFETTI_LIFETIME
    );
  }

  // Confetti from screen center: each piece is flung outward and falls, fading.
  burst() {
    if (this.#prefersReducedMotion) return;

    const count = 26;
    const colors = this.constructor.CONFETTI_COLORS;

    for (let i = 0; i < count; i++) {
      const piece = document.createElement("span");
      const color = colors[i % colors.length];
      const size = 7 + Math.random() * 6;

      piece.style.cssText = `
        position: absolute; left: 50%; top: 50%;
        width: ${size}px; height: ${size * 0.6}px;
        background: ${color}; border-radius: 1px;
        will-change: transform, opacity;
      `;
      this.element.appendChild(piece);

      const angle = (i / count) * 2 * Math.PI + (Math.random() - 0.5) * 0.4;
      const distance = 120 + Math.random() * 140;
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance - 60;
      const fall = 160 + Math.random() * 120;
      const spin = (Math.random() - 0.5) * 720;

      piece.animate(
        [
          { transform: "translate(-50%, -50%) rotate(0deg)", opacity: 1 },
          {
            transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) rotate(${spin / 2}deg)`,
            opacity: 1,
            offset: 0.35,
          },
          {
            transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy + fall}px)) rotate(${spin}deg)`,
            opacity: 0,
          },
        ],
        {
          duration: 1000 + Math.random() * 400,
          easing: "cubic-bezier(0.2, 0.6, 0.4, 1)",
          fill: "forwards",
        }
      );
    }
  }

  dismiss() {
    clearTimeout(this.timeout);
    clearTimeout(this.burstTimeout);
    this.element.style.transition = "opacity 300ms ease-out";
    this.element.style.opacity = "0";
    setTimeout(() => this.element.remove(), 300);
  }

  disconnect() {
    clearTimeout(this.timeout);
    clearTimeout(this.burstTimeout);
  }

  get #prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  get #animationDuration() {
    const raw = this.hasCometTarget ? getComputedStyle(this.cometTarget).animationDuration : "";
    const seconds = parseFloat(raw);
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 3000;
  }
}
